import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import GridLayout, { type Layout } from 'react-grid-layout';
import useSWR from 'swr';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { allWidgets, getWidget } from '@/widgets/registry';
import type { DashboardLayoutItem, WidgetContext } from '@/types/widget';
import { getUser } from '@/lib/auth';
import { PLUS_API_URL, SEITIATE_ENGINE_URL } from '@/lib/env';
import { apiFetch } from '@/lib/api';
import { PageTabBar, type PageTab } from '@/components/PageTabBar';

// Multi-page dashboard with backend persistence via SWR.
// Loads all pages on mount from GET /api/v1/dashboard/layouts.
// Auto-seeds defaults on first login (zero layouts).
// Each page has its own widget grid layout.
// Debounced save on layout change (drag/resize/add/remove) via PUT.

const COLS = 12;
const ROW_HEIGHT = 60;
const SAVE_DEBOUNCE_MS = 800;

function uid(): string {
  return `w_${Math.random().toString(36).slice(2, 10)}`;
}

// ── API types for dashboard layouts ───────────────────────────
interface BackendLayoutItem {
  i: string;
  widgetId: string;
  x: number;
  y: number;
  w: number;
  h: number;
  minW?: number;
  minH?: number;
  config?: Record<string, unknown>;
}

interface BackendLayoutPage {
  page_id: string;
  page_name: string;
  layout: BackendLayoutItem[];
  is_default: boolean;
  position: number;
  updated_at: string;
}

// ── Fetchers ──────────────────────────────────────────────────

async function fetchLayouts(): Promise<BackendLayoutPage[]> {
  return apiFetch<BackendLayoutPage[]>('/api/v1/dashboard/layouts');
}

async function seedDefaults(): Promise<BackendLayoutPage[]> {
  return apiFetch<BackendLayoutPage[]>('/api/v1/dashboard/layouts/seed-defaults', {
    method: 'POST',
  });
}

// Idempotent opt-in for users seeded before Lane 3 shipped (2026-05-03).
// Returns the existing agent-board page if it already exists; inserts it
// otherwise. See backend/app/api/v1/dashboard.py::seed_agent_board.
async function seedAgentBoard(): Promise<BackendLayoutPage> {
  return apiFetch<BackendLayoutPage>(
    '/api/v1/dashboard/layouts/seed-agent-board',
    { method: 'POST' },
  );
}

async function saveLayout(
  pageId: string,
  pageName: string,
  items: DashboardLayoutItem[],
  isDefault: boolean,
  position: number,
): Promise<void> {
  await apiFetch(`/api/v1/dashboard/layouts/${pageId}`, {
    method: 'PUT',
    body: JSON.stringify({
      page_name: pageName,
      layout: items.map((it) => ({
        i: it.i,
        widgetId: it.widgetId,
        x: it.x,
        y: it.y,
        w: it.w,
        h: it.h,
        minW: it.minW,
        minH: it.minH,
        config: it.config,
      })),
      is_default: isDefault,
      position: position,
    }),
  });
}

async function createPage(pageName: string): Promise<BackendLayoutPage> {
  return apiFetch<BackendLayoutPage>('/api/v1/dashboard/layouts', {
    method: 'POST',
    body: JSON.stringify({ page_name: pageName }),
  });
}

async function deletePage(pageId: string): Promise<void> {
  await apiFetch(`/api/v1/dashboard/layouts/${pageId}`, {
    method: 'DELETE',
  });
}

async function reorderPages(pages: { page_id: string; position: number }[]): Promise<void> {
  await apiFetch('/api/v1/dashboard/layouts/reorder', {
    method: 'PUT',
    body: JSON.stringify({ pages }),
  });
}

function backendToPageTab(p: BackendLayoutPage): PageTab {
  return {
    pageId: p.page_id,
    pageName: p.page_name,
    isDefault: p.is_default,
    position: p.position,
  };
}

function backendToItems(p: BackendLayoutPage): DashboardLayoutItem[] {
  return p.layout.map((it) => ({
    i: it.i,
    widgetId: it.widgetId,
    x: it.x,
    y: it.y,
    w: it.w,
    h: it.h,
    minW: it.minW,
    minH: it.minH,
    config: it.config,
  }));
}

// ── Component ─────────────────────────────────────────────────

export function DashboardPage() {
  const user = getUser();
  const [activePageId, setActivePageId] = useState<string | null>(null);
  const [itemsByPage, setItemsByPage] = useState<Record<string, DashboardLayoutItem[]>>({});
  const [pageTabs, setPageTabs] = useState<PageTab[]>([]);
  const [showPicker, setShowPicker] = useState(false);
  const [seeded, setSeeded] = useState(false);
  const [agentBoardSeedAttempted, setAgentBoardSeedAttempted] = useState(false);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Refs for debounced save access
  const itemsByPageRef = useRef(itemsByPage);
  itemsByPageRef.current = itemsByPage;
  const pageTabsRef = useRef(pageTabs);
  pageTabsRef.current = pageTabs;

  // SWR: load layouts
  const { data: layouts, error, isLoading, mutate } = useSWR(
    user ? 'dashboard-layouts' : null,
    fetchLayouts,
    { revalidateOnFocus: false },
  );

  // Auto-seed defaults on first login (zero layouts)
  useEffect(() => {
    if (isLoading || error || seeded) return;
    if (layouts && layouts.length === 0) {
      setSeeded(true);
      seedDefaults()
        .then((seededLayouts) => {
          mutate(seededLayouts, false);
        })
        .catch(() => {
          // Seed failed — continue with empty dashboard
        });
    }
  }, [layouts, isLoading, error, seeded, mutate]);

  // Auto-seed the Lane 3 Agent Board page for existing users who were
  // already past first-login when Lane 3 shipped (2026-05-03). The
  // seed_defaults() pathway only fires when layouts.length === 0, so
  // existing dashboards never get the new page automatically. The
  // backend endpoint is idempotent — if the page already exists this is
  // a no-op return of the existing row. We attempt at most once per
  // session (agentBoardSeedAttempted gates re-fires).
  useEffect(() => {
    if (isLoading || error || agentBoardSeedAttempted) return;
    if (!layouts || layouts.length === 0) return;
    if (layouts.some((p) => p.page_id === 'agent-board')) return;
    setAgentBoardSeedAttempted(true);
    seedAgentBoard()
      .then((agentBoardPage) => {
        // Append the new page to the cached layouts without re-fetching.
        mutate([...layouts, agentBoardPage], false);
      })
      .catch(() => {
        // Seed failed — leave the dashboard alone. The user can still
        // navigate the rest of their pages; we'll retry next session.
      });
  }, [layouts, isLoading, error, agentBoardSeedAttempted, mutate]);

  // Populate state from loaded/seeded layouts
  useEffect(() => {
    if (!layouts || layouts.length === 0) return;

    const tabs = layouts.map(backendToPageTab);
    setPageTabs(tabs);

    const byPage: Record<string, DashboardLayoutItem[]> = {};
    for (const p of layouts) {
      byPage[p.page_id] = backendToItems(p);
    }
    setItemsByPage(byPage);

    // Set active page: default page, or first page if no default
    setActivePageId((prev) => {
      // If prev is still valid, keep it
      if (prev && layouts.some((p) => p.page_id === prev)) return prev;
      const defaultPage = layouts.find((p) => p.is_default);
      return defaultPage ? defaultPage.page_id : layouts[0].page_id;
    });
  }, [layouts]);

  const activeItems = activePageId ? itemsByPage[activePageId] ?? [] : [];
  const activeTab = pageTabs.find((t) => t.pageId === activePageId);

  // Debounced save for the active page
  const debouncedSave = useCallback(
    (pageId?: string) => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => {
        const pid = pageId ?? activePageId;
        if (!pid) return;
        const tab = pageTabsRef.current.find((t) => t.pageId === pid);
        const items = itemsByPageRef.current[pid] ?? [];
        if (tab) {
          saveLayout(pid, tab.pageName, items, tab.isDefault, tab.position).catch(() => {
            // Save failed silently — layout still in memory
          });
        }
      }, SAVE_DEBOUNCE_MS);
    },
    [activePageId],
  );

  const context: WidgetContext = useMemo(
    () => ({
      userId: user?.uid ?? null,
      engineUserId: user?.engineUserId ?? null,
      tenantId: user?.tenantId ?? null,
      isDirector: Boolean(user?.isDirector),
      engineUrl: SEITIATE_ENGINE_URL,
      plusApiUrl: PLUS_API_URL,
      authToken: localStorage.getItem('cesauth_token'),
    }),
    [user],
  );

  const pickerOptions = useMemo(() => {
    const isDirector = Boolean(user?.isDirector);
    return allWidgets().filter((w) => {
      if (w.scope === 'both') return true;
      if (w.scope === 'director') return isDirector;
      return true;
    });
  }, [user]);

  const gridLayout: Layout[] = activeItems.map((it) => ({
    i: it.i,
    x: it.x,
    y: it.y,
    w: it.w,
    h: it.h,
    minW: it.minW,
    minH: it.minH,
  }));

  const onLayoutChange = (next: Layout[]) => {
    if (!activePageId) return;
    setItemsByPage((prev) => {
      const currentItems = prev[activePageId] ?? [];
      const updated = currentItems.map((it) => {
        const l = next.find((n) => n.i === it.i);
        return l ? { ...it, x: l.x, y: l.y, w: l.w, h: l.h } : it;
      });
      return { ...prev, [activePageId]: updated };
    });
    debouncedSave();
  };

  const addWidget = (widgetId: string) => {
    if (!activePageId) return;
    const def = getWidget(widgetId);
    if (!def) return;
    setItemsByPage((prev) => {
      const currentItems = prev[activePageId] ?? [];
      const placedY = currentItems.reduce((max, it) => Math.max(max, it.y + it.h), 0);
      return {
        ...prev,
        [activePageId]: [
          ...currentItems,
          {
            i: uid(),
            widgetId,
            x: 0,
            y: placedY,
            w: def.defaultSize.w,
            h: def.defaultSize.h,
            minW: def.minSize.w,
            minH: def.minSize.h,
            config: {},
          },
        ],
      };
    });
    setShowPicker(false);
    debouncedSave();
  };

  const removeWidget = (i: string) => {
    if (!activePageId) return;
    setItemsByPage((prev) => ({
      ...prev,
      [activePageId]: (prev[activePageId] ?? []).filter((it) => it.i !== i),
    }));
    debouncedSave();
  };

  const updateConfig = (i: string, next: Record<string, unknown>) => {
    if (!activePageId) return;
    setItemsByPage((prev) => ({
      ...prev,
      [activePageId]: (prev[activePageId] ?? []).map((it) =>
        it.i === i ? { ...it, config: next } : it,
      ),
    }));
    debouncedSave();
  };

  // ── Page tab handlers ───────────────────────────────────────

  const handleSelectPage = (pageId: string) => {
    setActivePageId(pageId);
    setShowPicker(false);
  };

  const handleRenamePage = (pageId: string, newName: string) => {
    setPageTabs((prev) =>
      prev.map((t) => (t.pageId === pageId ? { ...t, pageName: newName } : t)),
    );
    // Save immediately (not debounced — name change is intentional)
    const tab = pageTabsRef.current.find((t) => t.pageId === pageId);
    const items = itemsByPageRef.current[pageId] ?? [];
    if (tab) {
      saveLayout(pageId, newName, items, tab.isDefault, tab.position).catch(() => {});
    }
  };

  const handleDeletePage = async (pageId: string) => {
    try {
      await deletePage(pageId);
      // Remove from local state
      setPageTabs((prev) => prev.filter((t) => t.pageId !== pageId));
      setItemsByPage((prev) => {
        const next = { ...prev };
        delete next[pageId];
        return next;
      });
      // If we deleted the active page, switch to first remaining
      if (activePageId === pageId) {
        const remaining = pageTabsRef.current.filter((t) => t.pageId !== pageId);
        if (remaining.length > 0) {
          setActivePageId(remaining[0].pageId);
        }
      }
      // Refresh from backend to get updated is_default
      mutate();
    } catch {
      // Delete failed — leave state as-is
    }
  };

  const handleReorderPages = async (reordered: PageTab[]) => {
    setPageTabs(reordered);
    try {
      await reorderPages(
        reordered.map((p) => ({ page_id: p.pageId, position: p.position })),
      );
    } catch {
      // Reorder failed — leave optimistic state (will correct on next load)
    }
  };

  const handleCreatePage = async () => {
    const name = window.prompt('Page name:');
    if (!name?.trim()) return;
    try {
      const newPage = await createPage(name.trim());
      const tab = backendToPageTab(newPage);
      setPageTabs((prev) => [...prev, tab]);
      setItemsByPage((prev) => ({ ...prev, [newPage.page_id]: backendToItems(newPage) }));
      setActivePageId(newPage.page_id);
    } catch {
      // Create failed
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div style={{ padding: 24, color: 'var(--sp-text-muted)', fontSize: 13 }}>
        Loading dashboard...
      </div>
    );
  }

  return (
    <div>
      {/* Page tab bar */}
      <PageTabBar
        pages={pageTabs}
        activePageId={activePageId ?? ''}
        onSelectPage={handleSelectPage}
        onRenamePage={handleRenamePage}
        onDeletePage={handleDeletePage}
        onReorderPages={handleReorderPages}
        onCreatePage={handleCreatePage}
      />

      {/* Dashboard header */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12, marginTop: 12 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, color: 'var(--sp-text-muted)' }}>
            {activeTab?.pageName ?? 'Dashboard'} &middot; {activeItems.length} widget
            {activeItems.length === 1 ? '' : 's'}
          </div>
        </div>
        <button className="sp-btn sp-btn-primary" onClick={() => setShowPicker((v) => !v)}>
          + Add widget
        </button>
      </div>

      {showPicker && (
        <div
          style={{
            background: 'var(--sp-bg-elev)',
            border: '1px solid var(--sp-border)',
            borderRadius: 6,
            padding: 12,
            marginBottom: 16,
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
            gap: 8,
          }}
        >
          {pickerOptions.map((w) => (
            <button
              key={w.id}
              onClick={() => addWidget(w.id)}
              className="sp-btn"
              style={{ textAlign: 'left', padding: 10 }}
            >
              <div style={{ fontWeight: 600, color: 'var(--sp-text)' }}>{w.name}</div>
              <div style={{ color: 'var(--sp-text-muted)', fontSize: 12, marginTop: 2 }}>
                {w.description}
              </div>
              <div
                className="mono"
                style={{ color: 'var(--sp-text-dim)', fontSize: 11, marginTop: 6 }}
              >
                {w.id} &middot; {w.scope}
              </div>
            </button>
          ))}
        </div>
      )}

      {activeItems.length === 0 ? (
        <div className="sp-empty">
          No widgets yet. Click <strong>+ Add widget</strong> to start.
        </div>
      ) : (
        <GridLayout
          className="layout"
          layout={gridLayout}
          cols={COLS}
          rowHeight={ROW_HEIGHT}
          width={1200}
          onLayoutChange={onLayoutChange}
          draggableCancel=".sp-btn,button,input,textarea,a"
        >
          {activeItems.map((it) => {
            const def = getWidget(it.widgetId);
            if (!def) {
              return (
                <div key={it.i}>
                  <div className="sp-widget">
                    <div className="sp-widget-header">
                      <span>unknown: {it.widgetId}</span>
                      <button className="sp-btn" onClick={() => removeWidget(it.i)}>
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              );
            }
            const Component = def.component;
            return (
              <div key={it.i}>
                <div style={{ position: 'relative', height: '100%' }}>
                  <Component
                    config={it.config ?? {}}
                    onConfigChange={(next) => updateConfig(it.i, next)}
                    context={context}
                    size={{ w: it.w, h: it.h }}
                  />
                  <button
                    className="sp-btn"
                    onClick={() => removeWidget(it.i)}
                    style={{
                      position: 'absolute',
                      top: 6,
                      right: 6,
                      padding: '2px 6px',
                      fontSize: 11,
                    }}
                    aria-label="Remove widget"
                  >
                    &times;
                  </button>
                </div>
              </div>
            );
          })}
        </GridLayout>
      )}

      {error && (
        <div
          style={{
            marginTop: 12,
            padding: 8,
            fontSize: 11,
            color: '#f87171',
            background: 'rgba(248,113,113,0.1)',
            borderRadius: 4,
          }}
        >
          Failed to load dashboard layouts. Widgets shown from memory.
        </div>
      )}
    </div>
  );
}
